var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { createContext, useCallback, useRef } from 'react';
import { useFormik } from 'formik';
import { wrap } from '@hilma/tools';
import { errorGenerators, validators } from '../functions';
import { useBaseTranslate, useLanguage } from '../hooks';
import { confirmRegex } from '../constants';
export var FormContext = createContext(null);
export var FormRulesContext = createContext(null);
export var FormRefsContext = createContext(null);
export var OnSubmitRefContext = createContext(null);
export var GetRuleNameContext = createContext(null);
export var GetRuleRegexErrorContext = createContext(null);
var FormProvider = function (_a) {
    var initialValues = _a.initialValues, rules = _a.rules, _b = _a.refs, r = _b === void 0 ? initialValues : _b, children = _a.children;
    var refs = useRef(__assign({}, r));
    var onSubmitRef = useRef(null);
    var translate = useBaseTranslate();
    var language = useLanguage();
    var getRuleName = useCallback(function (rule) {
        var _a, _b;
        return (_b = (((_a = rule) === null || _a === void 0 ? void 0 : _a.name) instanceof Function ? translate((rule === null || rule === void 0 ? void 0 : rule.name) || '') : rule === null || rule === void 0 ? void 0 : rule.name)) !== null && _b !== void 0 ? _b : '';
    }, [translate]);
    var getRuleRegexError = useCallback(function (rule) {
        var _a;
        return (_a = ((rule === null || rule === void 0 ? void 0 : rule.activeRegexErr) instanceof Function ? translate((rule === null || rule === void 0 ? void 0 : rule.activeRegexErr) || '') : rule === null || rule === void 0 ? void 0 : rule.activeRegexErr)) !== null && _a !== void 0 ? _a : '';
    }, [translate]);
    var validate = useCallback(function (values) { return __awaiter(void 0, void 0, void 0, function () {
        var errors, key, rule, value, config;
        return __generator(this, function (_a) {
            errors = {};
            for (key in values) {
                rule = rules[key];
                value = values[key];
                if (!rule)
                    continue;
                config = { key: key, refs: refs, rule: rule, value: value, values: values };
                if (validators.isFileFromServer(config)) {
                    continue;
                }
                else if (rule.accept && !validators.isFile(config)) {
                    errors[key + "Path"] = errorGenerators.required(language);
                }
                else if (rule.before && !validators.isDateBefore(config)) {
                    errors[key] = errorGenerators.dateBefore(language, rule.before);
                }
                else if (rule.after && !validators.isDateAfter(config)) {
                    errors[key] = errorGenerators.dateAfter(language, rule.after);
                }
                else if (rule.before && !validators.isTimeBefore(config)) {
                    errors[key] = errorGenerators.timeBefore(language, rule.before);
                }
                else if (rule.after && !validators.isTimeAfter(config)) {
                    errors[key] = errorGenerators.timeAfter(language, rule.after);
                }
                else if (rule.minHourRange && !validators.isTimeMinRange(config)) {
                    errors[key] = errorGenerators.timeMinRange(language, rule.minHourRange);
                }
                else if (!validators.isStringGreaterThan(config)) {
                    errors[key] = errorGenerators.greaterThan(language, getRuleName(rule), rule.greaterThan);
                }
                else if (!validators.isStringSmallerThan(config)) {
                    errors[key] = errorGenerators.smallerThan(language, getRuleName(rule), rule.smallerThan);
                }
                else if (rule.length && !validators.isStringLength(config)) {
                    errors[key] = errorGenerators.length(language, getRuleName(rule), rule.length);
                }
                else if (!validators.isRequiredValue(config)) {
                    errors[key] = errorGenerators.required(language);
                }
                else if (rule.activeRegex && !validators.isMatchActiveRegex(config)) {
                    errors[key] = errorGenerators.regex(language, rule.name && getRuleName(rule), rule.activeRegexErr && getRuleRegexError(rule));
                }
                else if (!validators.isMatchRegexClassValidator(config)) {
                    errors[key] = errorGenerators.regex(language, rule.name && getRuleName(rule), rule.activeRegexErr && getRuleRegexError(rule));
                }
                else if (!validators.isConfirmValid(config)) {
                    errors[key] = errorGenerators.confirm(language);
                    errors[key.match(confirmRegex)[0].toLowerCase().substring(7, key.match(confirmRegex)[0].toLowerCase().length)] = errorGenerators.confirm(language);
                }
            }
            return [2 /*return*/, errors];
        });
    }); }, [language, getRuleName, getRuleRegexError, rules]);
    var formik = useFormik({
        initialValues: initialValues,
        validate: validate,
        onSubmit: onSubmitRef.current || (function () { })
    });
    return wrap([FormContext.Provider, { value: formik }], [FormRulesContext.Provider, { value: rules }], [FormRefsContext.Provider, { value: refs }], [OnSubmitRefContext.Provider, { value: onSubmitRef }], [GetRuleNameContext.Provider, { value: getRuleName }], [GetRuleRegexErrorContext.Provider, { value: getRuleRegexError }])(children);
};
export default FormProvider;
//# sourceMappingURL=FormProvider.js.map